import { t as translate } from 'i18n';
/** Pure query-data → ECharts options. Model options can decorate, never rebind data. */
import { buildChartData, buildPieData, formatValue, kpiValues, normalizeRows } from './widgetData';
import { formatAmount, formatShare, formatTick, unitOf, unitSymbol } from './units';
import { buildWorldMap } from './worldMap';

// Fallback only: the console passes the theme's data/series-1…4 (tokens.js).
const PALETTE = ['#4361ee', '#e86129', '#7c5cff', '#00b894'];
const CARTESIAN = ['line', 'bar', 'stacked', 'area', 'stacked_area', 'combo'];
const AXIS_TYPES = ['category', 'value', 'time', 'log'];
const ANNOTATIONS = new Set(['markLine', 'markPoint', 'markArea']);
const BLOCKED = new Set(['__proto__', 'constructor', 'prototype', 'data', 'dataset', 'source', 'encode', 'dimensions', 'transform']);
const SERIES_BINDING = new Set([
  'datasetIndex', 'datasetId', 'seriesLayoutBy',
  'coordinateSystem', 'xAxisIndex', 'xAxisId', 'yAxisIndex', 'yAxisId', 'geoIndex', 'geoId', 'calendarIndex', 'calendarId',
  'parallelIndex', 'parallelId', 'polarIndex', 'polarId', 'singleAxisIndex', 'singleAxisId', 'radarIndex', 'radarId', 'id',
]);
const MAP_BINDING = ['map', 'nameProperty', 'nameMap', 'mapValueCalculation'];
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const object = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const number = (value) => typeof value === 'number' && Number.isFinite(value);
const title = (column) => column?.title || column?.key || '';
const fail = (error = 'shape') => ({ option: null, error });
const axisOptions = (value) => Array.isArray(value) ? value : object(value) ? [value] : [];
const typeOf = (value) => axisOptions(value)[0]?.type;
const colour = (palette, index) => palette[index % palette.length];

/** Drop executable values, external image resources, prototype keys and data bindings. */
const seriesPath = (path) => path[0] === 'series' && (path.length === 1 || (path.length === 2 && typeof path[1] === 'number'));
const annotationPath = (path) => ANNOTATIONS.has(path[path.length - 1]) && seriesPath(path.slice(0, -1));

function clean(value, path = []) {
  const key = path.filter((part) => typeof part === 'string').at(-1) || '';
  if (value === null || typeof value === 'boolean' || number(value)) return value;
  if (typeof value === 'string') {
    if (/^(?:image:\/\/|javascript:|vbscript:|data:)|url\s*\(/i.test(value)) return undefined;
    if (/(?:image|icon|symbol|src|background)/i.test(key) && /^https?:\/\//i.test(value)) return undefined;
    return value;
  }
  if (Array.isArray(value)) return value.map((item, index) => clean(item, [...path, index]) ?? null);
  if (!object(value)) return undefined;
  const result = {};
  for (const [name, item] of Object.entries(value)) {
    if (BLOCKED.has(name) && !(name === 'data' && annotationPath(path))) continue;
    if (seriesPath(path) && SERIES_BINDING.has(name)) continue;
    const safe = clean(item, [...path, name]);
    if (safe !== undefined) result[name] = safe;
  }
  return result;
}

function merge(base, patch) {
  if (!object(patch)) return patch === undefined ? base : patch;
  const result = { ...(object(base) ? base : {}) };
  for (const [key, value] of Object.entries(patch)) {
    result[key] = object(value) ? merge(result[key], value) : value;
  }
  return result;
}

function groups(rows, key, column) {
  if (!key) return [{ name: '', rows }];
  const result = new Map();
  const labelCounts = new Map();
  for (const row of rows) {
    // Raw identities prevent formatting two different groups into one dataset.
    const identity = JSON.stringify([typeof row[key], row[key]]);
    if (!result.has(identity)) {
      const name = formatValue(row[key], column);
      result.set(identity, { name, value: row[key], rows: [] });
      labelCounts.set(name, (labelCounts.get(name) || 0) + 1);
    }
    result.get(identity).rows.push(row);
  }
  return [...result.values()].map((group) => ({ ...group, name: labelCounts.get(group.name) > 1 ? `${group.name} (${JSON.stringify(group.value) ?? 'undefined'})` : group.name }));
}

function categoryValues(rows, column, project = (value) => value) {
  const indices = new Map();
  const labels = [];
  const index = (value) => {
    const shown = project(value);
    const identity = JSON.stringify([typeof shown, shown]);
    if (!indices.has(identity)) { indices.set(identity, labels.length); labels.push(formatValue(shown, column)); }
    return indices.get(identity);
  };
  rows.forEach((row) => index(row[column.key]));
  return { labels, index };
}

function timeBucket(column) {
  if (column.type !== 'time' && column.kind !== 'time') return null;
  const suffix = /\.(day|hour)$/.exec(column.key);
  return {
    granularity: typeof column.granularity === 'string' ? column.granularity : suffix?.[1],
    member: suffix ? column.key.slice(0, -suffix[0].length) : null,
  };
}

/** Project clock hours only in a day × hour grid, never in an hourly timeline. */
function heatmapCategories(rows, column, otherColumn) {
  const part = object(column.calendar) && column.calendar.part;
  if (part === 'iso_weekday' || part === 'hour_of_day') {
    if (column.kind !== 'dimension' || column.type !== 'number') return null;
    const min = part === 'iso_weekday' ? 1 : 0;
    const count = part === 'iso_weekday' ? 7 : 24;
    if (rows.some((row) => !Number.isInteger(row[column.key]) || row[column.key] < min || row[column.key] >= min + count)) return null;
    const values = Array.from({ length: count }, (_, index) => min + index);
    const labels = part === 'iso_weekday' ? [translate('assistant.dashboardDateFieldMon'), translate('assistant.dashboardDateFieldTue'), translate('assistant.dashboardDateFieldWed'), translate('assistant.dashboardDateFieldThu'), translate('assistant.dashboardDateFieldFri'), translate('assistant.dashboardDateFieldSat'), translate('assistant.dashboardDateFieldSun')] : values.map((hour) => `${String(hour).padStart(2, '0')}:00`);
    // The semantic model has already extracted these coordinates in its
    // declared timezone. Keep numeric identities, without converting dates.
    return {
      values, labels, calendar: true, index: (value) => value - min,
      formatter: (value) => labels[Number(value) - min] ?? String(value),
    };
  }
  const bucket = timeBucket(column);
  const other = timeBucket(otherColumn);
  // Canonical Cube keys identify the underlying member. Metadata aliases may
  // not expose it; those still need an explicitly daily companion axis.
  const hourly = bucket?.granularity === 'hour' && other?.granularity === 'day'
    && (!bucket.member || !other.member || bucket.member === other.member);
  if (!hourly) return categoryValues(rows, column);
  // Read the bucket's own wall-clock hour, including offset/Z strings, without
  // converting it into the browser's timezone or rounding non-hourly values.
  const buckets = new Map();
  const hours = new Map();
  for (const row of rows) {
    const value = row[column.key];
    const match = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]([01]\d|2[0-3]):00(?::00(?:\.0+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/.exec(value);
    if (!match) return null;
    const hour = `${match[1]}:00`;
    buckets.set(value, hour);
    if (!hours.has(hour)) hours.set(hour, row);
  }
  // Sort at most 24 clock categories, rather than sorting/rebinding query rows.
  const ordered = [...hours].sort(([a], [b]) => a.localeCompare(b)).map(([, row]) => row);
  return { ...categoryValues(ordered, column, (value) => buckets.get(value)), hourly: true };
}

function dimensionKey(chart, columns) {
  const rest = columns.filter((column) => column.kind !== 'measure' && column.key !== chart.x);
  const key = chart.series || (rest.length === 1 ? rest[0].key : null);
  if (rest.some((column) => column.key !== key)) return { error: 'shape' };
  return { key };
}

function measureName(unit, titles) {
  const suffix = unitSymbol(unit);
  const labels = [...new Set(titles)].map((label) => suffix && label.endsWith(`, ${suffix}`) ? label.slice(0, -suffix.length - 2) : label);
  return [labels.join(', '), suffix].filter(Boolean).join(', ');
}

function textStyle(theme, color = theme.muted) {
  return { color, fontFamily: theme.fontFamily, fontSize: 12, fontWeight: 400, textBorderWidth: 0, textShadowBlur: 0 };
}

function axisStyle(theme) {
  return {
    axisLabel: textStyle(theme), nameTextStyle: textStyle(theme),
    axisTick: { show: false }, minorTick: { show: false },
    axisLine: { show: false, lineStyle: { color: theme.border } },
  };
}

function numericAxis(unit, titles, index, count, horizontal, theme) {
  const name = count > 1 ? measureName(unit, titles) : unitSymbol(unit);
  return {
    id: `measure-${index}`, type: 'value', name,
    ...(unit === 'currency' ? { minInterval: 1 } : {}),
    position: horizontal ? index === 0 ? 'bottom' : 'top' : index === 0 ? 'left' : 'right',
    offset: index > 1 ? (index - 1) * 58 : 0,
    // Horizontal bars reserve width for category names. Build a sparse value
    // scale first; hideOverlap remains a fallback for especially narrow cards.
    ...axisStyle(theme), splitNumber: horizontal ? 2 : 4,
    axisLabel: { ...textStyle(theme), hideOverlap: true, formatter: (value) => formatTick(value, unit) },
    splitLine: { show: index === 0, lineStyle: { color: theme.border, width: 1, type: 'dashed' } },
    minorSplitLine: { show: false },
  };
}

function nativeAxesFit(options, xCount, yCount) {
  return axisOptions(options.xAxis).length <= xCount && axisOptions(options.yAxis).length <= yCount;
}

function cartesian(chart, columns, rows, palette, theme, options) {
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const requestedX = typeOf(options.xAxis);
  const requestedY = typeOf(options.yAxis);
  let horizontal = requestedY === 'category' && (requestedX === 'value' || requestedX === 'log');
  if (horizontal && !['bar', 'stacked'].includes(chart.kind)) return fail();
  const xType = horizontal ? 'category' : requestedX || 'category';
  if (!AXIS_TYPES.includes(xType) || (!horizontal && requestedY && !['value', 'log'].includes(requestedY))) return fail();
  const stacked = ['stacked', 'stacked_area'].includes(chart.kind);
  const allUnits = [...new Set(chart.y.map((key) => unitOf(byKey[key])))];
  if (stacked && allUnits.length > 1) return fail('units');
  let data;
  if (xType === 'category') {
    data = buildChartData({ ...chart, kind: stacked ? 'stacked' : chart.kind }, columns, rows, palette, { maxUnits: 3 });
    if (data.fallback) return fail(data.fallback);
  } else {
    const grouping = dimensionKey(chart, columns);
    if (grouping.error) return fail(grouping.error);
    const xColumn = byKey[chart.x];
    const coordinate = (row) => row[chart.x];
    if (rows.some((row) => xType === 'time'
      ? !(typeof coordinate(row) === 'string' || number(coordinate(row))) || !Number.isFinite(new Date(coordinate(row)).getTime())
      : !number(coordinate(row)) || (xType === 'log' && coordinate(row) <= 0))) return fail();
    const axes = allUnits.map((unit, index) => ({ id: index ? `y${index}` : 'y', unit, titles: chart.y.filter((key) => unitOf(byKey[key]) === unit).map((key) => title(byKey[key])) }));
    const datasets = [];
    for (const group of groups(rows, grouping.key, byKey[grouping.key])) {
      const seen = new Set();
      if (group.rows.some((row) => { const identity = xType === 'time' ? new Date(coordinate(row)).getTime() : coordinate(row); if (seen.has(identity)) return true; seen.add(identity); return false; })) return fail();
      for (const key of chart.y) datasets.push({
        label: grouping.key ? chart.y.length > 1 ? `${group.name} · ${title(byKey[key])}` : group.name : title(byKey[key]),
        data: group.rows.map((row) => [coordinate(row), number(row[key]) ? row[key] : null]),
        colour: colour(palette, datasets.length), axis: axes[allUnits.indexOf(unitOf(byKey[key]))].id, unit: unitOf(byKey[key]),
      });
    }
    data = { axes, datasets };
    // Numeric/time axes retain raw coordinates, including sub-day timestamps.
    if (!xColumn) return fail();
  }
  const xColumn = byKey[chart.x];
  // Axis appearance also expresses orientation: never move a supplied max,
  // formatter or rotation from the category axis onto the measurement axis.
  const explicitAxes = own(options, 'xAxis') || own(options, 'yAxis');
  if (chart.kind === 'bar' && !explicitAxes && allUnits.length === 1
    && !['time', 'measure'].includes(xColumn.kind) && !['time', 'number'].includes(xColumn.type)
    && !rows.some((row) => number(row[chart.x]))
    && data.labels?.length <= 12 && data.labels.some((label) => String(label).length > 18)) horizontal = true;
  if (!nativeAxesFit(options, horizontal ? data.axes.length : 1, horizontal ? 1 : data.axes.length)) return fail();
  const category = {
    type: xType, ...(data.labels ? { data: data.labels } : {}),
    ...(['value', 'log'].includes(xType) ? { name: unitSymbol(byKey[chart.x]), ...(unitOf(byKey[chart.x]) === 'currency' ? { minInterval: 1 } : {}) } : {}),
    ...axisStyle(theme), splitLine: { show: false },
    ...(horizontal ? { inverse: true } : {}),
    axisLabel: { ...textStyle(theme), ...(['value', 'log'].includes(xType) ? { formatter: (value) => formatTick(value, byKey[chart.x]) } : {}),
      ...(horizontal ? { width: 132, overflow: 'break', interval: 0, lineHeight: 17, margin: 12 } : {}) },
  };
  const axes = data.axes.map((axis, index) => numericAxis(axis.unit, axis.titles, index, data.axes.length, horizontal, theme));
  const requestedMeasures = axisOptions(horizontal ? options.xAxis : options.yAxis);
  if (requestedMeasures.some((axis) => axis?.type && !['value', 'log'].includes(axis.type))) return fail();
  const series = data.datasets.map((dataset, index) => {
    const axisIndex = data.axes.findIndex((axis) => axis.id === dataset.axis);
    const numericType = requestedMeasures[axisIndex]?.type || requestedMeasures[0]?.type || 'value';
    const values = dataset.data.map((value) => Array.isArray(value) ? value[1] : value);
    if (numericType === 'log' && values.some((value) => value !== null && value <= 0)) return null;
    // Grouped datasets repeat y in the same order for every category group.
    // A semantic combo must still mix bars and lines when options are omitted.
    const type = ['bar', 'stacked'].includes(chart.kind)
      || (chart.kind === 'combo' && index % chart.y.length === 0) ? 'bar' : 'line';
    return {
      id: `bound-series-${index}`, name: dataset.label, type,
      ...(horizontal ? { xAxisIndex: axisIndex, yAxisIndex: 0 } : { xAxisIndex: 0, yAxisIndex: axisIndex }),
      data: dataset.data,
      itemStyle: { color: dataset.colour, borderRadius: chart.kind === 'stacked' ? 0 : 4, borderWidth: 0 }, lineStyle: { color: dataset.colour, width: 2 },
      label: textStyle(theme, theme.ink),
      ...(stacked ? { stack: 'bound-stack' } : {}),
      ...(['area', 'stacked_area'].includes(chart.kind) ? { areaStyle: { opacity: 0.12 } } : {}),
      barMaxWidth: 32, ...(type === 'line' ? { connectNulls: false, symbol: 'circle', symbolSize: 4 } : {}),
    };
  });
  if (series.some((item) => item === null) || !series.some((item) => item.data.some((value) => number(Array.isArray(value) ? value[1] : value)))) return fail();
  const legendVisible = series.length > 1 || axisOptions(options.legend).some((legend) => legend?.show === true);
  const sliderVisible = axisOptions(options.dataZoom).some((zoom) => object(zoom) && (!zoom.type || zoom.type === 'slider') && zoom.show !== false && zoom.orient !== 'vertical');
  return {
    option: {
      legend: { show: series.length > 1 },
      grid: { left: 16, right: horizontal ? 24 : 16 + Math.max(0, axes.length - 1) * 62, top: (legendVisible ? 42 : 24) + (horizontal ? Math.max(0, axes.length - 1) * 42 : 0), bottom: sliderVisible ? 64 : horizontal ? 36 : 24, containLabel: true },
      xAxis: horizontal ? axes : [category], yAxis: horizontal ? [category] : axes, series,
      ...(data.labels?.length > 30 ? { dataZoom: [{ type: 'inside', zoomOnMouseWheel: 'ctrl', moveOnMouseWheel: false }] } : {}),
    },
    units: data.datasets.map((dataset) => dataset.unit), trigger: 'axis', valueIndex: xType === 'category' ? null : 1,
  };
}

function slices(chart, columns, rows, palette, kind, theme) {
  // Pie legends and funnel stage lists render a DOM entry for every row.
  // Treemaps draw their labels inside the canvas and do not use those lists.
  const pie = buildPieData(chart, columns, rows, palette, kind === 'treemap' ? { maxSlices: Infinity } : undefined);
  if (pie.fallback) return fail(kind === 'pie' || kind === 'doughnut' ? 'pie' : 'shape');
  const items = pie.slices.map((slice, index) => ({ name: slice.label, value: slice.value, itemStyle: { color: colour(palette, index) } }));
  const label = { ...textStyle(theme, theme.ink), lineHeight: 17, overflow: 'break', backgroundColor: theme.surface, borderRadius: 4, padding: [3, 5] };
  if (kind === 'funnel') return { option: { legend: { show: false }, series: [{ id: 'bound-series-0', type: 'funnel', sort: 'none', top: 16, bottom: 16, left: '8%', width: '84%', gap: 6, data: items, itemStyle: { borderWidth: 0 }, label: { ...textStyle(theme, theme.ink), show: false, position: 'right' }, labelLine: { show: false }, emphasis: { label: { ...textStyle(theme, theme.ink), show: false }, itemStyle: { shadowBlur: 0 } } }] }, units: [pie.unit] };
  // Treemap labels fill their node's text box; a solid background would cover
  // the colour of the entire tile rather than just the category name.
  const treemapLabel = { ...label, backgroundColor: 'transparent' };
  if (kind === 'treemap') return { option: { legend: { show: false }, series: [{ id: 'bound-series-0', type: 'treemap', top: 8, bottom: 8, left: 8, right: 8, roam: false, nodeClick: false, breadcrumb: { show: false }, data: items, itemStyle: { borderColor: theme.surface, borderWidth: 2, gapWidth: 2 }, label: { ...treemapLabel, position: 'insideTopLeft', formatter: '{b}' }, emphasis: { label: treemapLabel, itemStyle: { shadowBlur: 0 } } }] }, units: [pie.unit] };
  const itemStyle = kind === 'doughnut'
    ? { borderRadius: 8, borderWidth: 2, borderColor: theme.surface }
    : { borderWidth: 0 };
  return { option: { legend: { show: false }, series: [{ id: 'bound-series-0', name: pie.title, type: 'pie', radius: kind === 'doughnut' ? ['52%', '78%'] : '78%', center: ['50%', '50%'], data: items, itemStyle, label: { ...textStyle(theme, theme.ink), show: false, formatter: '{b}' }, labelLine: { show: false }, emphasis: { scaleSize: 3, itemStyle: { shadowBlur: 0 } } }] }, units: [pie.unit], shares: true };
}

function pointChart(chart, columns, rows, palette, theme, options) {
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const xColumn = byKey[chart.x];
  const yColumn = byKey[chart.y[0]];
  const sizeColumn = chart.kind === 'bubble' ? byKey[chart.size] : null;
  if (chart.y.length !== 1 || (chart.kind === 'bubble' && !sizeColumn)) return fail();
  const xType = typeOf(options.xAxis) || (xColumn.kind === 'measure' || xColumn.type === 'number' ? 'value' : xColumn.type === 'time' ? 'time' : 'category');
  const yType = typeOf(options.yAxis) || 'value';
  if (!AXIS_TYPES.includes(xType) || !['value', 'log'].includes(yType) || !nativeAxesFit(options, 1, 1)) return fail();
  const categories = xType === 'category' ? categoryValues(rows, xColumn) : null;
  if (rows.some((row) => !number(row[yColumn.key]) || (yType === 'log' && row[yColumn.key] <= 0)
    || (sizeColumn && (!number(row[sizeColumn.key]) || row[sizeColumn.key] < 0))
    || (xType === 'time' && (!(typeof row[xColumn.key] === 'string' || number(row[xColumn.key])) || !Number.isFinite(new Date(row[xColumn.key]).getTime())))
    || (['value', 'log'].includes(xType) && (!number(row[xColumn.key]) || (xType === 'log' && row[xColumn.key] <= 0))))) return fail();
  const maxSize = sizeColumn ? rows.reduce((max, row) => Math.max(max, row[sizeColumn.key]), 0) : 1;
  const detailColumns = columns.filter((column) => column.kind !== 'measure' && column.key !== chart.x && column.key !== chart.series);
  const series = groups(rows, chart.series, byKey[chart.series]).map((group, index) => ({
    id: `bound-series-${index}`, type: 'scatter', name: group.name || title(yColumn),
    data: group.rows.map((row) => ({
      name: detailColumns.map((column) => `${title(column)}: ${formatValue(row[column.key], column)}`).join(' · ') || formatValue(row[chart.x], xColumn),
      value: [categories ? categories.index(row[chart.x]) : row[chart.x], row[yColumn.key], ...(sizeColumn ? [row[sizeColumn.key]] : [])],
    })),
    itemStyle: { color: colour(palette, index), opacity: 0.8 }, label: textStyle(theme, theme.ink),
    symbolSize: sizeColumn ? (value) => maxSize > 0 ? 8 + Math.sqrt(Math.max(0, value[2]) / maxSize) * 42 : 8 : 10,
  }));
  return {
    option: {
      grid: { left: 72, right: 28, top: 48, bottom: 52, containLabel: true },
      xAxis: [{
        type: xType, name: title(xColumn), nameLocation: 'middle', nameGap: 30,
        ...(categories ? { data: categories.labels } : {}),
        ...axisStyle(theme), splitLine: { show: false },
        axisLabel: { ...textStyle(theme), ...(!categories && xType !== 'time' ? { formatter: (value) => formatTick(value, xColumn) } : {}) },
      }],
      yAxis: [{
        ...numericAxis(unitOf(yColumn), [title(yColumn)], 0, 1, false, theme),
        name: measureName(unitOf(yColumn), [title(yColumn)]), nameLocation: 'middle', nameGap: 50,
      }],
      series,
    },
    units: series.map(() => unitOf(yColumn)), valueIndex: 1, xColumn, sizeColumn, categories: categories?.labels,
  };
}

function heatmap(chart, columns, rows, palette, theme, options) {
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const xColumn = byKey[chart.x];
  const groupColumn = byKey[chart.series];
  const yColumn = byKey[chart.y[0]];
  if (!groupColumn || chart.y.length !== 1 || columns.some((column) => column.kind !== 'measure' && ![chart.x, chart.series].includes(column.key))
    || [typeOf(options.xAxis), typeOf(options.yAxis)].some((type) => type && type !== 'category') || !nativeAxesFit(options, 1, 1)) return fail();
  const x = heatmapCategories(rows, xColumn, groupColumn);
  const y = heatmapCategories(rows, groupColumn, xColumn);
  if (!x || !y) return fail();
  const seen = new Set();
  const data = rows.map((row) => {
    const a = x.index(row[chart.x]); const b = y.index(row[chart.series]); const value = row[yColumn.key];
    const key = `${a}:${b}`;
    if (seen.has(key) || !number(value)) return null;
    seen.add(key); return [a, b, value];
  });
  if (data.some((item) => item === null)) return fail();
  const bounds = data.reduce(([min, max], item) => [Math.min(min, item[2]), Math.max(max, item[2])], [Infinity, -Infinity]);
  const calendarGrid = x.calendar || y.calendar;
  const axis = (categories, vertical) => ({
    type: 'category', data: categories.values || categories.labels, ...axisStyle(theme),
    ...(vertical && (categories.hourly || categories.calendar) ? { inverse: true } : {}),
    axisLabel: {
      ...textStyle(theme), hideOverlap: true,
      ...(categories.formatter ? { formatter: categories.formatter } : {}),
      ...(categories.calendar && (vertical || categories.values.length === 7) ? { interval: 0 } : {}),
    },
    splitArea: { show: false },
    // Only real cells carry a value/colour. Grid lines leave missing calendar
    // combinations visible as empty cells, rather than invented zeroes.
    ...(calendarGrid ? { splitLine: { show: true, interval: 0, lineStyle: { color: theme.border, width: 1 } } } : {}),
  });
  return {
    option: { legend: { show: false }, grid: { left: 16, right: 16, top: 16, bottom: 64, containLabel: true }, xAxis: [axis(x, false)], yAxis: [axis(y, true)], visualMap: { min: bounds[0], max: bounds[1], calculable: true, orient: 'horizontal', left: 'center', bottom: 0, textStyle: textStyle(theme), inRange: { color: [theme.raised, colour(palette, 0)] }, formatter: (value) => formatTick(value, yColumn) }, series: [{ id: 'bound-series-0', name: title(yColumn), type: 'heatmap', data, label: textStyle(theme, theme.ink), itemStyle: { borderColor: theme.surface, borderWidth: 1 }, emphasis: { itemStyle: { borderColor: theme.border, borderWidth: 1, shadowBlur: 0 } } }] },
    units: [unitOf(yColumn)], valueIndex: 2, heatmapLabels: { x: x.labels, y: y.labels },
  };
}

function gauge(chart, columns, rows, palette, theme) {
  const column = columns.find((item) => item.key === chart.y[0]);
  if (chart.y.length !== 1 || rows.length !== 1 || !number(rows[0][column.key])) return fail();
  const value = rows[0][column.key];
  // Keep the scale above the pivot and the exact value/name below it. Pixel
  // offsets preserve their separation even in the minimum-height card.
  return {
    option: {
      legend: { show: false },
      series: [{
        id: 'bound-series-0', type: 'gauge', radius: '64%', center: ['50%', '50%'], startAngle: 180, endAngle: 0,
        min: Math.min(0, value), max: Math.max(unitOf(column) === 'percent' ? 100 : 1, value), splitNumber: 4,
        axisLine: { lineStyle: { width: 12, color: [[1, theme.raised]] } }, progress: { show: true, width: 12, roundCap: true },
        itemStyle: { color: colour(palette, 0) }, axisTick: { show: false }, splitLine: { show: false, length: 0, distance: 0 },
        axisLabel: { ...textStyle(theme), distance: -20, formatter: (amount) => formatTick(amount) },
        pointer: { width: 4, length: '58%' },
        title: { ...textStyle(theme, theme.ink), fontWeight: 500, width: 200, overflow: 'break', lineHeight: 17, offsetCenter: [0, 62] },
        detail: { ...textStyle(theme, theme.ink), fontWeight: 600, fontSize: 26, lineHeight: 30, padding: 0, width: 'auto', formatter: (amount) => formatAmount(amount, column), offsetCenter: [0, 26] },
        data: [{ name: title(column), value }],
      }],
    }, units: [unitOf(column)],
  };
}

function kpi(chart, columns, rows, theme) {
  const tiles = kpiValues(chart, columns, rows);
  if (tiles.fallback) return fail('kpi');
  return { option: { legend: { show: false }, graphic: tiles.flatMap((tile, index) => [
    { type: 'text', left: 24, top: 24 + index * 76, style: { text: tile.title, fill: theme.muted, fontFamily: theme.fontFamily, fontSize: 12, fontWeight: 400 } },
    { type: 'text', left: 24, top: 46 + index * 76, style: { text: tile.value, fill: theme.ink, fontFamily: theme.fontFamily, fontSize: 26, fontWeight: 600 } },
  ]) }, units: [] };
}

function tooltipFormatter(result) {
  return (input) => {
    const items = Array.isArray(input) ? input : [input];
    const header = result.trigger === 'axis' ? items[0]?.axisValueLabel : null;
    const lines = header == null ? [] : [String(header)];
    for (const item of items) {
      if (!item) continue;
      const value = item.value ?? item.data?.value ?? item.data;
      const unit = result.units[item.seriesIndex || 0] || '';
      if (result.heatmapLabels && Array.isArray(value)) {
        lines.push(`${result.heatmapLabels.x[value[0]]} · ${result.heatmapLabels.y[value[1]]}: ${formatAmount(value[2], unit)}`);
      } else if (result.xColumn && Array.isArray(value)) {
        if (item.name) lines.push(String(item.name));
        const x = result.categories ? result.categories[value[0]] : result.xColumn.type === 'time' ? String(value[0]) : formatAmount(value[0], result.xColumn);
        lines.push(`${title(result.xColumn)}: ${x}`, `${item.seriesName || ''}: ${formatAmount(value[1], unit)}`);
        if (result.sizeColumn) lines.push(`${title(result.sizeColumn)}: ${formatAmount(value[2], result.sizeColumn)}`);
      } else if (result.mapLabels) {
        const label = result.mapLabels[item.name] || item.name || '';
        lines.push(`${label}: ${number(value) ? formatAmount(value, unit) : translate('assistant.answerWidgetNoData')}`);
      } else {
        const amount = Array.isArray(value) ? value[result.valueIndex ?? value.length - 1] : value;
        const name = result.trigger === 'axis' ? item.seriesName : item.name || item.seriesName;
        lines.push(`${name || ''}: ${formatAmount(amount, unit)}${result.shares && number(item.percent) ? ` (${formatShare(item.percent)})` : ''}`);
      }
    }
    // renderMode:richText draws these strings; no HTML is generated or interpreted.
    return lines.join('\n');
  };
}

function overlay(result, options, kind, theme) {
  const safe = clean(options) || {};
  const { series: appearance, xAxis, yAxis, tooltip, geo, ...rest } = safe;
  const option = merge(result.option, rest);
  if (kind !== 'choropleth' && geo !== undefined) option.geo = geo;
  for (const [key, patch] of [['xAxis', xAxis], ['yAxis', yAxis]]) {
    if (!result.option[key]) continue;
    option[key] = result.option[key].map((axis, index) => {
      const item = Array.isArray(patch) ? patch[index] : patch;
      if (!object(item)) return axis;
      // A common style may apply to every generated unit axis; its ID identifies
      // just the first one, so it must not create duplicate component IDs.
      if (!Array.isArray(patch) && index > 0) {
        const { id, ...style } = item;
        return merge(axis, style);
      }
      return merge(axis, item);
    });
  }
  if (result.option.series) option.series = result.option.series.map((series, index) => {
    const supplied = Array.isArray(appearance) ? appearance[index] : appearance;
    // A world map has one trusted geography. Geo appearance decorates that
    // series instead of creating a separate, unbound geo component.
    const geoStyle = Array.isArray(geo) ? geo[0] : geo;
    const patch = kind === 'choropleth' ? merge(object(geoStyle) ? geoStyle : {}, supplied) : supplied;
    if (!object(patch)) return series;
    const { type, ...style } = patch;
    if (kind === 'choropleth') [...MAP_BINDING, ...SERIES_BINDING].forEach((key) => { delete style[key]; });
    if (kind === 'bubble') delete style.symbolSize;
    const merged = merge(series, { ...style, ...(kind === 'combo' && ['line', 'bar'].includes(type) ? { type } : {}) });
    if (kind === 'funnel' && object(style.label) && style.label.show === true && !own(style.emphasis?.label || {}, 'show')) {
      merged.emphasis = merge(merged.emphasis, { label: { show: true } });
    }
    for (const annotation of ANNOTATIONS) {
      if (object(merged[annotation])) merged[annotation] = {
        ...merged[annotation],
        label: { ...textStyle(theme, theme.ink), ...(annotation === 'markLine' ? { position: 'insideEnd' } : {}), ...(object(merged[annotation].label) ? merged[annotation].label : {}) },
      };
    }
    return merged;
  });
  if (kind === 'choropleth') {
    option.series = option.series.map((series, index) => ({ ...series, ...Object.fromEntries(MAP_BINDING.map((key) => [key, result.option.series[index][key]])) }));
    const bindScale = (scale) => {
      const bound = { ...scale, dimension: 0, seriesIndex: 0 };
      if (bound.type !== 'piecewise' && number(bound.min) && bound.min === bound.max) {
        const colors = bound.inRange?.color;
        const color = Array.isArray(colors) ? colors.at(-1) : result.option.visualMap.pieces?.[0]?.color || result.option.color?.[0];
        return { ...bound, type: 'piecewise', text: undefined, itemHeight: 12,
          pieces: [{ value: bound.min, label: formatAmount(bound.min, result.units[0]), ...(color ? { color } : {}) }] };
      }
      return bound;
    };
    option.visualMap = Array.isArray(option.visualMap) ? option.visualMap.map(bindScale) : bindScale(option.visualMap);
  }
  if (kind === 'kpi') option.graphic = result.option.graphic;
  option.tooltip = {
    ...merge({ trigger: result.trigger || 'item', confine: true, backgroundColor: theme.chrome, borderColor: theme.chrome, borderWidth: 0, borderRadius: 6, padding: [8, 10], shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0, textStyle: { ...textStyle(theme, theme.onChrome), lineHeight: 18 } }, tooltip),
    renderMode: 'richText', formatter: typeof tooltip?.formatter === 'string' ? tooltip.formatter : tooltipFormatter(result),
  };
  option.animation = typeof safe.animation === 'boolean' ? safe.animation : false;
  option.aria = { ...merge({ enabled: true }, safe.aria), enabled: true };
  return option;
}

/** {option} or {option:null,error:shape|units|pie|kpi}; never mutates its inputs. */
export function buildEChartsOption(chart, columns, inputRows, inputPalette, inputTheme = {}) {
  if (!object(chart) || !Array.isArray(chart.y) || !chart.y.length || !Array.isArray(columns)) return fail();
  const validColumns = columns.filter((column) => object(column) && typeof column.key === 'string');
  const byKey = Object.fromEntries(validColumns.map((column) => [column.key, column]));
  if (validColumns.length !== columns.length || new Set(validColumns.map((column) => column.key)).size !== validColumns.length
    || validColumns.some((column) => column.title != null && typeof column.title !== 'string')
    || chart.y.some((key) => !own(byKey, key)) || new Set(chart.y).size !== chart.y.length
    || (chart.x != null && !own(byKey, chart.x)) || (chart.series != null && !own(byKey, chart.series)) || (chart.size != null && !own(byKey, chart.size))) return fail();
  if (!['kpi', 'gauge'].includes(chart.kind) && !byKey[chart.x]) return fail();
  const rows = normalizeRows(inputRows, validColumns);
  if (!rows.length) return fail(chart.kind === 'kpi' ? 'kpi' : 'shape');
  if (rows.some((row) => [chart.x, chart.series].some((key) => key != null && row[key] != null && typeof row[key] === 'object'))) return fail();
  const options = object(chart.options) ? chart.options : {};
  if ([...axisOptions(options.xAxis), ...axisOptions(options.yAxis)].some((axis) => object(axis) && own(axis, 'type') && !AXIS_TYPES.includes(axis.type))) return fail();
  const palette = Array.isArray(inputPalette) ? inputPalette.filter((item) => typeof item === 'string' && clean(item, ['backgroundColor']) !== undefined) : [];
  const nativeColours = Array.isArray(options.color) ? options.color.map((item) => clean(item, ['color'])).filter((item) => typeof item === 'string' || object(item)) : [];
  const colours = nativeColours.length ? nativeColours : palette.length ? palette : PALETTE;
  const theme = {
    fontFamily: inputTheme?.fontFamily || "'Manrope', system-ui, sans-serif",
    ink: inputTheme?.ink || inputTheme?.textColor || '#17171a', muted: inputTheme?.inkMuted || '#5c5c55', border: inputTheme?.border || '#ebeae4',
    chrome: inputTheme?.chrome || '#17171a', onChrome: inputTheme?.onChrome || '#ffffff', surface: inputTheme?.surface || '#ffffff', raised: inputTheme?.raised || '#f1f1ec',
  };
  if (chart.kind === 'combo' && axisOptions(options.series).some((series) => series?.type && !['line', 'bar'].includes(series.type))) return fail();
  let result;
  if (CARTESIAN.includes(chart.kind)) result = cartesian(chart, validColumns, rows, colours, theme, options);
  else if (['pie', 'doughnut', 'funnel', 'treemap'].includes(chart.kind)) result = slices(chart, validColumns, rows, colours, chart.kind, theme);
  else if (['scatter', 'bubble'].includes(chart.kind)) result = pointChart(chart, validColumns, rows, colours, theme, options);
  else if (chart.kind === 'heatmap') result = heatmap(chart, validColumns, rows, colours, theme, options);
  else if (chart.kind === 'gauge') result = gauge(chart, validColumns, rows, colours, theme);
  else if (chart.kind === 'choropleth') result = buildWorldMap(chart, validColumns, rows, colours, theme);
  else if (chart.kind === 'kpi') result = kpi(chart, validColumns, rows, theme);
  else return fail();
  if (result.error) return result;
  result.option = {
    color: colours, textStyle: textStyle(theme, theme.ink),
    ...result.option,
    legend: { type: 'scroll', top: 0, left: 0, right: 0, icon: 'circle', itemWidth: 8, itemHeight: 8, itemGap: 14, textStyle: textStyle(theme, theme.ink), pageTextStyle: textStyle(theme), pageIconSize: 10, pageIconColor: theme.muted, pageIconInactiveColor: theme.border, ...result.option.legend },
  };
  return { option: overlay(result, options, chart.kind, theme), ...(result.unmatched ? { unmatched: result.unmatched } : {}) };
}
